import React, { useState } from 'react';
import { Button, ButtonGroup, Popper, MenuItem, MenuList, ClickAwayListener, Grow, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Stack, Paper, Checkbox, Typography, Box } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import { useNotify, useRecordContext, useTranslate, useGetOne, useGetIdentity } from 'react-admin';
import { getToken } from '../authentication/authProvider';
import { Funeral } from '../types';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

const CertificateDropdownButton = () => {
  const record = useRecordContext<Funeral>();
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();

  // Fetch primary client
  const { data: primaryClient, isLoading: isPrimaryClientLoading } = useGetOne(
    'clients',
    { id: record?.primary_client_id },
    { enabled: !!record?.primary_client_id }
  );

  // Dropdown state
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Modal fields
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // Add state for attachments UI
  const [availableAttachments, setAvailableAttachments] = useState<Array<{ label: string; url: string; checked: boolean; required?: boolean }>>([
    { label: 'Certificate PDF', url: '', checked: true, required: true },
  ]);

  // Dropdown options
  const options = [
    { label: translate('resources.funerals.certificate.certificate') || 'Print Certificate', icon: <PrintIcon />, action: 'print' },
    { label: 'Send Certificate', icon: <EmailIcon />, action: 'send' },
  ];

  // Print logic (from CertificateButton)
  const displayReport = async (id: number | string, locale: string) => {
    try {
      const token = await getToken();
      const result = await fetch(
        PAPERMAN_API_URL + `funerals/${locale}/${id}/certificate`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (result.status >= 200 && result.status < 300) {
        const blob = await result.blob();
        window.open(URL.createObjectURL(blob), '_blank');
      } else {
        notify(translate('resources.funerals.certificate._error'));
      }
    } catch (e: any) {
      console.warn(e);
      notify(e.message ?? JSON.stringify(e));
    }
  };

  // Open modal and prepopulate fields
  const handleSendClick = () => {
    if (!record) return;
    // Prepopulate To with primary client email if available
    setTo(primaryClient?.email || '');
    setCc('');
    const certUrl = `${PAPERMAN_API_URL}funerals/en/${record.id}/certificate`;
    setSubject(`Cleo - Documents`);
    
    // Set body text based on funeral language and region
    const isFrench = record.language === 'fr';
    const isOntario = record.region === 'ontario';
    const userName = identity?.fullName || 'Cleo Team';
    
    // Choose the appropriate checklist based on region and language
    let checklistUrl: string;
    let checklistLabel: string;
    
    if (isOntario) {
      // Ontario uses English checklist
      checklistUrl = 'https://dlwlfzjarwyddyxkahfw.supabase.co/storage/v1/object/public/files//Cleo%20Cremation%20-%20Checklist%20ON.pdf';
      checklistLabel = 'Cleo Cremation Checklist ON';
    } else {
      // Quebec uses language-based checklist
      checklistUrl = isFrench 
        ? 'https://dlwlfzjarwyddyxkahfw.supabase.co/storage/v1/object/public/files//Cleo%20-%20Liste%20de%20verification%20apres%20un%20deces%20QC.pdf'
        : 'https://dlwlfzjarwyddyxkahfw.supabase.co/storage/v1/object/public/files//Cleo%20Cremation%20-%20Checklist%20QC.pdf';
      checklistLabel = isFrench ? 'Cleo - Liste de verification QC' : 'Cleo Cremation Checklist QC';
    }
    
    // Set body text based on region
    let bodyText: string;
    
    if (isOntario) {
      // Ontario - English only
      bodyText = `Hello ${primaryClient?.first_name || ''},

Please find attached our Proof of Death document. This document can be used as valid proof of death with many institutions while waiting for the official documents to arrive.

I have also included a checklist that may be helpful in settling the estate.

If you need anything else or have any questions, please don't hesitate to let me know.

Thank you,
${userName}`;
    } else {
      // Quebec - bilingual based on language preference
      bodyText = isFrench 
        ? `Bonjour ${primaryClient?.first_name || ''},

Vous trouverez ci-joint notre document d'attestation de décès. Ce document peut être utilisé comme preuve de décès valide auprès de plusieurs institutions en attendant l'arrivée des documents officiels.

J'ai également inclus une liste de vérification qui pourrait vous être utile dans le règlement de la succession.

Si vous avez besoin de quoi que ce soit d'autre ou si vous avez des questions, n'hésitez pas à me le faire savoir.

Merci,
${userName}`
        : `Hello ${primaryClient?.first_name || ''},

Please find attached our Attestation of Death document. This document can be used as valid proof of death with many institutions while waiting for the official documents to arrive.

I have also included a checklist that may be helpful in settling the estate.

If you need anything else or have any questions, please don't hesitate to let me know.

Thank you,
${userName}`;
    }
    
    setBody(bodyText);
    // Set up attachments: certificate always included, checklist based on region/language
    setAvailableAttachments([
      { label: 'Certificate PDF', url: certUrl, checked: true, required: true },
      { label: checklistLabel, url: checklistUrl, checked: true, required: false },
    ]);
    setModalOpen(true);
  };

  // Send certificate email
  const handleSend = async () => {
    if (!record) return;
    setSending(true);
    try {
      // Only include non-certificate attachments (like checklist)
      // The backend will generate the certificate PDF internally
      const attachments = availableAttachments
        .filter(a => a.checked && a.label !== 'Certificate PDF')
        .map(a => ({ url: a.url, filename: a.label + '.pdf' }));
      
      // Prepare payload
      const payload: any = {
        to,
        subject,
        body,
      };
      if (cc.trim()) payload.cc = cc;
      // Only include attachments if we have non-certificate ones (like checklist)
      if (attachments.length > 0) {
        payload.attachments = attachments;
      }
      // Note: Not including certificate URL - backend will generate it internally

      const response = await fetch(`${PAPERMAN_API_URL}funerals/${record.id}/certificate-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send email');
      }

      notify('Certificate sent successfully', { type: 'success' });
      setModalOpen(false);
    } catch (e: any) {
      console.error('Send error:', e);
      notify(e.message || 'Failed to send certificate', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  // Dropdown handlers
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };
  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };
  const handleMenuItemClick = (action: string) => {
    setOpen(false);
    if (action === 'print') {
      void displayReport(Number(record.id), 'en');
    } else if (action === 'send') {
      handleSendClick();
    }
  };

  if (!record) return null;

  return (
    <>
      <ButtonGroup variant="outlined" ref={anchorRef} aria-label="certificate split button">
        <Button startIcon={<PrintIcon />} onClick={() => void displayReport(Number(record.id), 'en')}>
          {translate('resources.funerals.certificate.certificate') || 'Print Certificate'}
        </Button>
        <Button
          size="small"
          aria-controls={open ? 'certificate-split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select certificate action"
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 10 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="certificate-split-button-menu" autoFocusItem>
                  {options.map((option) => (
                    <MenuItem
                      key={option.action}
                      onClick={() => handleMenuItemClick(option.action)}
                    >
                      {option.icon}
                      <span style={{ marginLeft: 8 }}>{option.label}</span>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Certificate to Client</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="To"
              value={to}
              onChange={e => setTo(e.target.value)}
              fullWidth
              required
              disabled={isPrimaryClientLoading}
              helperText={isPrimaryClientLoading ? 'Loading primary client...' : ''}
            />
            <TextField
              label="CC"
              value={cc}
              onChange={e => setCc(e.target.value)}
              fullWidth
              placeholder="Optional, comma-separated"
            />
            <TextField
              label="Subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Body"
              value={body}
              onChange={e => setBody(e.target.value)}
              fullWidth
              multiline
              minRows={5}
              required
            />
            <Box sx={{ mb: 2, mt: 2, bgcolor: '#f9fafb', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>Attachments:</Typography>
              {availableAttachments.map((att, idx) => (
                <Box key={att.label} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={att.checked}
                      disabled={att.required}
                      onChange={e => {
                        setAvailableAttachments(prev => prev.map((a, i) => i === idx ? { ...a, checked: e.target.checked } : a));
                      }}
                      color="primary"
                      sx={{ color: '#5c68e0', '&.Mui-checked': { color: '#5c68e0' } }}
                    />
                    <Typography sx={{ fontWeight: 500 }}>{att.label}</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (att.label === 'Certificate PDF') {
                        void displayReport(Number(record.id), 'en');
                      } else {
                        window.open(att.url, '_blank');
                      }
                    }}
                    sx={{ borderRadius: 6, px: 3, py: 0.5, color: '#5c68e0', borderColor: '#5c68e0', textTransform: 'uppercase', fontWeight: 500, fontSize: '0.875rem' }}
                  >
                    Preview
                  </Button>
                </Box>
              ))}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={loading || sending}>Cancel</Button>
          <Button onClick={handleSend} variant="contained" color="primary" disabled={loading || sending}>
            {loading || sending ? <CircularProgress size={20} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CertificateDropdownButton; 